import { isSameYear } from 'date-fns';

import { dateFnsLocales, locales } from './constants';
import formatRelative from './formatRelative';

type FormatRelativeLocale = {
  lastWeek: string;
  nextWeek: string;
  other: string;
  today: string;
  tomorrow: string;
  yesterday: string;
};

/**
 * Conditionally format relative date
 * - remove time from relative date
 * - remove year if date is in the same year as baseDate
 *
 * @param {number | Date} date
 * @param {number | Date} baseDate - default to now
 * @returns {string} relatively formatted date
 *
 * @example
 * "aujourd’hui"
 * "samedi dernier"
 * "13 décembre"
 * "13 décembre 2020"
 */
const formatCalendar = (
  date: number | Date,
  baseDate: number | Date = new Date(),
): string => {
  const language = dateFnsLocales[locales.getLanguage()];
  const isFrench = language === dateFnsLocales.fr;

  const defaultFormat = isFrench ? 'E dd LLL' : 'E. dd LLL';
  let dateFormat = defaultFormat;

  if (!isSameYear(date, baseDate)) {
    dateFormat = `${dateFormat} yyyy`;
  }

  const formatRelativeLocale: FormatRelativeLocale = isFrench
    ? {
        lastWeek: dateFormat,
        nextWeek: dateFormat,
        other: dateFormat,
        today: "'Aujourd’hui'",
        tomorrow: "'Demain'",
        yesterday: "'Hier'",
      }
    : {
        lastWeek: dateFormat,
        nextWeek: dateFormat,
        other: dateFormat,
        today: "'Today'",
        tomorrow: "'Tomorrow'",
        yesterday: "'Yesterday'",
      };

  return formatRelative(date, baseDate, {
    locale: {
      ...language,
      formatRelative: (token: keyof FormatRelativeLocale) =>
        formatRelativeLocale[token],
    },
  });
};

export default formatCalendar;
